import React from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';

const UserTable = ({
  loading,
  users,
  filteredUsers,
  searchTerm,
  roleFilter,
  companies,
  openEditModal,
  setShowDeleteConfirm,
  getRoleDisplayName,
  getRoleColor,
  getUsernameFromEmail
}) => {
  // Get company names for display
  const getCompanyNames = (userCompanies) => {
    if (!userCompanies || userCompanies.length === 0) {
      return 'None';
    }
    return userCompanies.map(c => c.name).join(', ');
  };

  // Get managed user names for display
  const getManagedUserNames = (user) => {
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      return 'N/A';
    }
    
    if (!user.managed_users || user.managed_users.length === 0) {
      return 'None';
    }
    
    return user.managed_users.map(u => u.email).join(', ');
  };

  // Get managed user count for display
  const getManagedUserCount = (user) => {
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      return '-';
    }
    return user.managed_users?.length || 0;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                USER
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                EMAIL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                ROLE
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                COMPANIES
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                MANAGED USERS
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                STATUS
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                CREATED
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading && users.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                    <span className="mt-4 text-gray-500 dark:text-gray-400">Loading users...</span>
                  </div>
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  {searchTerm || roleFilter !== 'all' 
                    ? 'No users found matching your criteria' 
                    : 'No users found. Click "Add User" to create one.'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                        user.role === 'superadmin' ? 'bg-gradient-to-r from-purple-600 to-pink-600' :
                        user.role === 'admin' ? 'bg-gradient-to-r from-blue-600 to-cyan-600' :
                        'bg-gradient-to-r from-green-600 to-emerald-600'
                      }`}>
                        {user.email.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {getUsernameFromEmail(user.email)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {user.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${getRoleColor(user.role)}`}>
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 max-w-xs" title={getCompanyNames(user.companies)}>
                      {user.role === 'superadmin' ? 'All Companies' : getCompanyNames(user.companies)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {user.role === 'superadmin' ? `${companies.length} companies total` : `${user.companies?.length || 0} company(s)`}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 max-w-xs" title={getManagedUserNames(user)}>
                      {getManagedUserNames(user)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {getManagedUserCount(user)} user(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => openEditModal(user)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Edit user"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => setShowDeleteConfirm(user)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete user"
                        disabled={user.role === 'superadmin'}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;